/** subscription.status */
export enum SubscriptionStatus {
  Active = 'active',
  PastDue = 'past_due',
  Canceled = 'canceled',
  Trialing = 'trialing',
}

/** V2 subscription contract lifecycle status.
 *
 * Order is significant: it must match the `subscription_contract_status`
 * enum's `enumsortorder` in Postgres. Originally `Canceled` was last, but
 * `Migration20260424210000` added `dunning` and `suspended` at the end via
 * `ALTER TYPE ADD VALUE`, which placed them AFTER the pre-existing
 * `canceled` in `pg_enum.enumsortorder`. Postgres has no clean reorder
 * mechanism, so the TS declaration is aligned to the DB rather than vice
 * versa. */
export enum SubscriptionContractStatus {
  None = 'none',
  Active = 'active',
  GracePeriod = 'grace_period',
  PastDue = 'past_due',
  Canceled = 'canceled',
  Dunning = 'dunning',
  Suspended = 'suspended',
}

/** User/admin subscription command action */
export enum BillingSubscriptionCommandAction {
  Cancel = 'cancel',
  Resume = 'resume',
  Change = 'change',
  Noop = 'noop',
}

/** Admin status override sentinel */
export enum BillingContractStatusDirective {
  Preserve = 'preserve',
}

/** Contract fields directly repairable by the billing admin API */
export enum BillingContractRepairField {
  ProviderCustomerId = 'providerCustomerId',
  ProviderSubscriptionId = 'providerSubscriptionId',
  CurrPlanId = 'currPlanId',
  CurrPlanProviderId = 'currPlanProviderId',
  CurrCadence = 'currCadence',
  CurrCadenceStart = 'currCadenceStart',
  CurrCadenceEnd = 'currCadenceEnd',
  DeffPlanId = 'deffPlanId',
  DeffCadence = 'deffCadence',
  CurrContractStatus = 'currContractStatus',
  LedgePaidUntil = 'ledgePaidUntil',
  LedgeGracePlanId = 'ledgeGracePlanId',
  LedgeGraceUntil = 'ledgeGraceUntil',
  LedgeDunningEntryPlanId = 'ledgeDunningEntryPlanId',
  TrialConsumedAt = 'trialConsumedAt',
  ProviderLcLastEventId = 'providerLcLastEventId',
  ProviderLcLastEventAt = 'providerLcLastEventAt',
  ProviderLcStatus = 'providerLcStatus',
}

/** Scheduled change type exposed by subscription current-state API */
export enum SubscriptionScheduledChangeType {
  Downgrade = 'downgrade',
  Cancel = 'cancel',
}

/** subscription history event types */
export enum BillingSubscriptionEventType {
  Subscribed = 'subscribed',
  Renewed = 'renewed',
  Upgraded = 'upgraded',
  Downgraded = 'downgraded',
  ScheduledCancel = 'scheduled_cancel',
  ScheduledChange = 'scheduled_change',
  Canceled = 'canceled',
  Expired = 'expired',
  Paused = 'paused',
  Resumed = 'resumed',
  PaymentFailed = 'payment_failed',
  PaymentRecovered = 'payment_recovered',
  ManualSync = 'manual_sync',
  AdminChange = 'admin_change',
}

/** Eligibility reason code for subscription usage decisions */
export enum TotalEligibilityReasonCode {
  Eligible = 'ELIGIBLE',
  TrialEligible = 'TRIAL_ELIGIBLE',
  NoAvailableLimit = 'NO_AVAILABLE_LIMIT',
  ContractInactive = 'CONTRACT_INACTIVE',
  UsageProviderUnavailable = 'USAGE_PROVIDER_UNAVAILABLE',
}

export const SUBSCRIPTION_STATUS_VALUES = Object.freeze(Object.values(SubscriptionStatus));

export const SUBSCRIPTION_SCHEDULED_CHANGE_TYPE_VALUES = Object.freeze(Object.values(SubscriptionScheduledChangeType));

export const TOTAL_ELIGIBILITY_REASON_CODE_VALUES = Object.freeze(Object.values(TotalEligibilityReasonCode));

export const BILLING_SUBSCRIPTION_COMMAND_ACTION_VALUES = Object.freeze(Object.values(BillingSubscriptionCommandAction));

export const BILLING_CONTRACT_STATUS_DIRECTIVE_VALUES = Object.freeze(Object.values(BillingContractStatusDirective));

export const BILLING_CONTRACT_REPAIR_FIELD_VALUES = Object.freeze(Object.values(BillingContractRepairField));
